import type {SourceEntityRecord, SourceEntityRequest} from 'src/api/ProactApiClient';
import {EntityTypeEnum} from 'src/api/ProactApiClient';
import {selectAllAudienceLabel} from 'src/app/assignment-rules/assignmentRuleHelpers';

export type PersonnelSelectionValue =
  | SourceEntityRequest
  | SourceEntityRecord[]
  | undefined
  | null;

interface CountConfig {
  entityType: EntityTypeEnum;
  singular: string;
  plural: string;
}

// Fixed display order and singular/plural nouns for each counted dimension.
const countConfigs: CountConfig[] = [
  {entityType: EntityTypeEnum.Employee, singular: 'employee', plural: 'employees'},
  {entityType: EntityTypeEnum.Manager, singular: 'manager', plural: 'managers'},
  {entityType: EntityTypeEnum.Department, singular: 'department', plural: 'departments'},
  {entityType: EntityTypeEnum.Group, singular: 'group', plural: 'groups'},
  {entityType: EntityTypeEnum.Vendor, singular: 'vendor', plural: 'vendors'},
  {entityType: EntityTypeEnum.Project, singular: 'project', plural: 'projects'},
  {entityType: EntityTypeEnum.Attribute, singular: 'targeted person', plural: 'targeted people'},
];

export function describePersonnelSelection(value: PersonnelSelectionValue): string {
  const selectAllEntitiesType = Array.isArray(value)
    ? undefined
    : value?.selectAllEntitiesType;
  const entities = Array.isArray(value) ? value : value?.entities ?? [];

  if (selectAllEntitiesType) {
    return selectAllAudienceLabel(selectAllEntitiesType);
  }

  if (!entities.length) {
    return 'No selections';
  }

  const counts = new Map<EntityTypeEnum, number>();
  entities.forEach(entity => {
    counts.set(entity.entityType, (counts.get(entity.entityType) ?? 0) + 1);
  });

  return countConfigs
    .map(config => {
      const count = counts.get(config.entityType) ?? 0;
      if (count === 0) return undefined;
      const noun = count === 1 ? config.singular : config.plural;
      return `${count.toLocaleString('en-US')} ${noun}`;
    })
    .filter((part): part is string => part !== undefined)
    .join(', ');
}
